/**
 * Simplified Web Components wrapper - for debugging freeze issue
 * Minimal implementation without extra features
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import { logger } from '@/application/core';
import { UserContextProvider } from './components/common/UserContextProvider';

/**
 * Parse attribute value to appropriate JavaScript type
 */
function parseAttributeValue(value: string): any {
  try {
    return JSON.parse(value);
  } catch {
    if (value === 'true') return true;
    if (value === 'false') return false;
    return value;
  }
}

/**
 * Extract props from element attributes
 */
function extractProps(element: HTMLElement): Record<string, any> {
  const props: Record<string, any> = {};

  for (let i = 0; i < element.attributes.length; i++) {
    const attr = element.attributes[i];
    if (['id', 'class', 'style'].includes(attr.name)) continue;

    // Convert kebab-case to camelCase
    const propName = attr.name.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
    props[propName] = parseAttributeValue(attr.value);
  }

  return props;
}

/**
 * Simple Web Component registration - NO extra features
 */
export function registerSimpleComponent(
  tagName: string,
  ReactComponent: React.ComponentType<any>
): void {
  if (customElements.get(tagName)) {
    logger.warn('Simple', `${tagName} already registered`);
    return;
  }

  class SimpleReactComponent extends HTMLElement {
    private root: ReactDOM.Root | null = null;

    connectedCallback() {
      logger.debug('Simple', `${tagName} connected`);

      // Extract props and render immediately - NO setTimeout
      const props = extractProps(this);
      logger.debug('Simple', 'Props:', Object.keys(props));

      // Create root and render - wrapped in UserContextProvider
      this.root = ReactDOM.createRoot(this);
      this.root.render(
        React.createElement(
          UserContextProvider,
          null,
          React.createElement(ReactComponent, props)
        )
      );

      logger.debug('Simple', `${tagName} rendered`);
    }

    disconnectedCallback() {
      if (this.root) {
        this.root.unmount();
        this.root = null;
      }
    }
  }

  customElements.define(tagName, SimpleReactComponent);
  logger.debug('Simple', `Registered ${tagName}`);
}
